import React, { useState, useEffect, useMemo   } from 'react';
import { Modal, Calendar, Spin, Empty, Tooltip, Button, Popover } from 'antd';
import { CloseOutlined, DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import './ClientDetailModal.css';
import axios from 'axios';
import cubejs from '@cubejs-client/core';
import moment from 'moment';
import { handleQueryClientViewCalendar } from './index';
import Incentive from './Incentive';


const ClientDetailModal = ({ visible, onClose, clientId, clientName, podName, currentMonth, currentUser, modality,isrTargetAchieved }) => {
  const [loading, setLoading] = useState(false);
  const [calendarData, setCalendarData] = useState({});
  const selectedMonth =useMemo(() => new Date(currentMonth), [currentMonth]);
  const [clientSummary, setClientSummary] = useState({
    totalAmount: 0,
    totalCases: 0,
    activeDays: 0,
    avgPerDay: 0,
    avgPerCase: 0
  });
  
  // Fetch data when modal opens
  useEffect(() => {
    const cubejsApi = cubejs(
        process.env.REACT_APP_MARKET_SHARE_GET_STATE_WISE_DATA_TOKEN,
        {
            apiUrl: process.env.REACT_APP_MARKET_SHARE_GET_STATE_WISE_DATA,
        }
    );
    if (visible && clientId && podName) {
      fetchClientCalendarData(cubejsApi);
    }
  }, [visible, clientId, podName, selectedMonth]);
  
  // Calculate summary data

  const getPreviousDate = (date) => {
    return moment(date).subtract(1, 'days').format('YYYY-MM-DD');
  };
  
  // Fetch client calendar data
  const fetchClientCalendarData = async (cubejsApi) => {
    setLoading(true);
    try {
      // Format month for API
      const startDate = currentMonth.startOf('month').format('YYYY-MM-DD')
        const previousDate = getPreviousDate(startDate);
        // const last11month = moment(startDate).subtract(11, 'months').format('YYYY-MM-DD')
        const endDate = currentMonth.endOf('month').format('YYYY-MM-DD')
        await cubejsApi
        .load(handleQueryClientViewCalendar(clientId, previousDate, endDate, modality))
        .then((resultSet) => {
            // const modifiedData = resultSet?.loadResponses[0]?.data?.map(data => {
            //     return {
            //         date: data['Contribution.Date'],
            //         case_count: data['Contribution.total_case_count'],
            //     }
            // })
            const pivotedData = resultSet.tablePivot({
                x: ["Contribution.Date", "Contribution.total_case_count"],
                y: ["Contribution.modality"]
            })
            // console.log(pivotedData,'pivotedData')
            const modifiedData = pivotedData.map(data => {
                return {
                    date: data['Contribution.Date'],
                    case_count: parseInt(data?.['XRAY,Contribution.total_case_count']||0)+parseInt(data?.['CT,Contribution.total_case_count']||0)+parseInt(data?.['MRI,Contribution.total_case_count']||0)+parseInt(data?.['NM,Contribution.total_case_count']||0),
                    XRAY: parseInt(data?.['XRAY,Contribution.total_case_count']||0),
                    CT: parseInt(data?.['CT,Contribution.total_case_count']||0),
                    MRI: parseInt(data?.['MRI,Contribution.total_case_count']||0),
                    NM: parseInt(data?.['NM,Contribution.total_case_count']||0),
                    amount: parseInt(data?.['XRAY,Contribution.incentive_amount']||0)+parseInt(data?.['CT,Contribution.incentive_amount']||0)+parseInt(data?.['MRI,Contribution.incentive_amount']||0)+parseInt(data?.['NM,Contribution.incentive_amount']||0),
                    modalities: {
                        XRAY: parseInt(data?.['XRAY,Contribution.incentive_amount']||0),
                        CT: parseInt(data?.['CT,Contribution.incentive_amount']||0),
                        MRI: parseInt(data?.['MRI,Contribution.incentive_amount']||0),
                        NM: parseInt(data?.['NM,Contribution.incentive_amount']||0)
                    }
                }
            })
     
            setClientSummary({
                totalAmount: modifiedData?.reduce((acc, curr) => acc + parseInt(curr?.amount), 0),
                totalCases: modifiedData?.reduce((acc, curr) => acc + parseInt(curr?.case_count), 0),
                activeDays: modifiedData?.length,
                avgPerDay: modifiedData?.reduce((acc, curr) => acc + parseInt(curr?.amount), 0) / modifiedData?.length,
                avgPerCase: modifiedData?.reduce((acc, curr) => acc + parseInt(curr?.amount), 0) / modifiedData?.reduce((acc, curr) => acc + parseInt(curr?.case_count), 0)
            })
            // console.log(modifiedData,'modifiedData')
            setCalendarData(generateMockData(modifiedData))
        })
        .catch((error) => {
            console.error("Error fetching API response:", error);
        })
      
      // Uncomment this when you have a real API
      // setCalendarData(response.data);
    } catch (error) {
      console.error('Error fetching client calendar data:', error);
      // Use mock data on error for development
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth() + 1;
      const mockData = generateMockData(year, month);
      setCalendarData(mockData);
    } finally {
      setLoading(false);
    }
  };
  
  // Generate mock data for development
  const generateMockData = (modifiedData) => {
    const data = {};
    const daysInMonth = currentMonth?.daysInMonth();
    const currentInsights = modifiedData
    // //console.log(podData,'currentInsights')
    //console.log(calendarData,'hello')
    //console.log(daysInMonth,'daysInMonth')

    for (let i = 1; i <= daysInMonth; i++) {
      // Skip some days randomly to simulate days without data
      // if (Math.random() > 0.7) continue;
      
      const dayDate = currentMonth.clone().date(i).format('YYYY-MM-DD');
      const isWeekend = currentMonth.clone().date(i).day() === 0 || currentMonth.clone().date(i).day() === 6;
      
      // Generate lower amounts for weekends
      const basecount= currentInsights?.find(item => item?.date?.split('T')[0] === dayDate)?.case_count
      const baseAmount= currentInsights?.find(item => item?.date?.split('T')[0] === dayDate)?.amount
      const baseXRAY= currentInsights?.find(item => item?.date?.split('T')[0] === dayDate)?.XRAY
      const baseCT= currentInsights?.find(item => item?.date?.split('T')[0] === dayDate)?.CT
      const baseMRI= currentInsights?.find(item => item?.date?.split('T')[0] === dayDate)?.MRI
      const baseNM= currentInsights?.find(item => item?.date?.split('T')[0] === dayDate)?.NM
      
      data[dayDate] = {
        amount: baseAmount,
        count: basecount,
        XRAY: baseXRAY,
        CT: baseCT,
        MRI: baseMRI,
        NM: baseNM,
        modalities: {
            XRAY: currentInsights?.find(item => item?.date?.split('T')[0] === dayDate)?.modalities?.XRAY,
            CT: currentInsights?.find(item => item?.date?.split('T')[0] === dayDate)?.modalities?.CT,
            MRI: currentInsights?.find(item => item?.date?.split('T')[0] === dayDate)?.modalities?.MRI,
            NM: currentInsights?.find(item => item?.date?.split('T')[0] === dayDate)?.modalities?.NM
        }
      };
    }
    return data
  };
  
  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Date', 'Case Count', 'Amount'];
    const dataRows = Object.entries(calendarData).map(([date, data]) => [
      date,
      data.count,
      data.amount
    ]);
    
    const csvContent = [
      headers.join(','),
      ...dataRows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${clientName}_${clientId}_calendar.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

 
  
  // Custom calendar cell renderer
  const dateCellRender = (date) => {
    const dateStr = date.format('YYYY-MM-DD');
    const dayData = calendarData[dateStr];
    
    if (!dayData) return null;
    
    const intensity = Math.min(dayData.amount / 5000, 1); // Adjust based on your data range
    
    return ( dayData?.count>0 ?(
      <motion.div 
        className="client-day-data"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        style={{
          backgroundColor: `rgba(59, 130, 246, ${0.1 + intensity * 0.2})`,
          borderLeft: '3px solid #3B82F6',
          backgroundColor: 'aliceblue'
        }}
      >
         <Popover 
                        content={<ModalityBreakdown isrTargetAchieved={isrTargetAchieved} dayData={dayData} />}
                        title="Modality Breakdown"
                        trigger="hover"
                      >
                        <div className="client-day-amount">₹{isrTargetAchieved>0 ? <> {dayData?.amount?.toLocaleString()}</> : 0}</div>
                        <div className="client-day-count">{dayData?.count} {dayData?.count === 1 ? 'Case' : 'Cases'}</div>
                      </Popover>
      </motion.div>
    ) : null
)
  };
  
  // Handle month change
//   const onPanelChange = (date) => {
//     setSelectedMonth(date.toDate());
//   };
  
  return (
    <Modal
      title={
        <div className="client-modal-title">
          <div className="client-info">
            <span className="client-name-modal">{clientName}</span>
            <span className="client-id-modal">ID: {clientId}</span>
            <span className="client-pod">Pod: {podName?.name}</span>
          </div>
     
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={'60%'}
      className="client-detail-modal"
      closeIcon={<CloseOutlined />}
    >
      <div className="client-modal-content">
        {/* <div className="client-summary-cards">
          <div className="summary-card">
            <div className="summary-card-value">${}</div>
            <div className="summary-card-label">Total Amount</div>
          </div>
          <div className="summary-card">
            <div className="summary-card-value">{clientSummary.totalCases}</div>
            <div className="summary-card-label">Total Cases</div>
          </div>
          <div className="summary-card">
            <div className="summary-card-value">{clientSummary.activeDays}</div>
            <div className="summary-card-label">Active Days</div>
          </div>
          <div className="summary-card">
            <div className="summary-card-value">${clientSummary.avgPerCase.toLocaleString()}</div>
            <div className="summary-card-label">
              Avg Per Case
              <Tooltip title="Total amount divided by total cases">
                <InfoCircleOutlined className="info-icon" />
              </Tooltip>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-card-value">${clientSummary.avgPerDay.toLocaleString()}</div>
            <div className="summary-card-label">
              Avg Per Day
              <Tooltip title="Total amount divided by active days">
                <InfoCircleOutlined className="info-icon" />
              </Tooltip>
            </div>
          </div>
        </div> */}
        
        {loading ? (
          <div className="loading-container">
            <Spin size="large" />
            <p>Loading calendar data...</p>
          </div>
        ) : Object.keys(calendarData).length > 0 ? (
          <div className="client-calendar-container">
            <Calendar 
              dateCellRender={dateCellRender}
            //   headerRender={() => null} 
            //        mode="month"
            // //   onPanelChange={onPanelChange}
            // value={currentMonth}  
            // value={currentMonth}
            validRange={[
                moment(currentMonth).clone().startOf('month'),
                moment(currentMonth).clone().endOf('month'),
            ]}
              className="client-calendar"
            />
          </div>
        ) : (
          <Empty 
            description="No calendar data available for this client" 
            className="empty-data"
          />
        )}
      </div>
    </Modal>
  );
};


const ModalityBreakdown = ({ isrTargetAchieved, dayData }) => (
    <div className="modality-breakdown">

      <div className="breakdown-header">
        <span>Modality</span>
        <span>Cases</span>
        <span>Amount</span>
      </div>
  
            {dayData?.XRAY > 0 && (
              <div key={dayData?.XRAY} className="breakdown-row">
                <span className={`modality-name XRAY`}>
                  XRAY
              </span>
              <span className="modality-count">{dayData?.XRAY}</span>
              <span className="modality-amount">
                ₹{isrTargetAchieved>0 ? <> {dayData?.modalities?.XRAY?.toLocaleString()}</> : 0}
              </span>
            </div>
            )}
            {dayData?.CT > 0 && (
              <div key={dayData?.CT} className="breakdown-row">
                <span className={`modality-name CT`}>
                  CT
              </span>
              <span className="modality-count">{dayData?.CT}</span>
              <span className="modality-amount">
                ₹{isrTargetAchieved>0 ? <> {dayData?.modalities?.CT?.toLocaleString()}</> : 0}
              </span>
            </div>
            )}
  
            {dayData?.MRI > 0 && (
              <div key={dayData?.MRI} className="breakdown-row">
                <span className={`modality-name MRI`}>
                  MRI
              </span>
              <span className="modality-count">{dayData?.MRI}</span>
              <span className="modality-amount">
                ₹{isrTargetAchieved>0 ? <> {dayData?.modalities?.MRI?.toLocaleString()}</> : 0}
              </span>
            </div>
            )}
            {dayData?.NM > 0 && (
              <div key={dayData?.NM} className="breakdown-row">
                <span className={`modality-name NM`}>
                  NM
              </span>
              <span className="modality-count">{dayData?.NM}</span>
              <span className="modality-amount">
                ₹{isrTargetAchieved>0 ? <> {dayData?.modalities?.NM?.toLocaleString()}</> : 0}
              </span>
            </div>
            )}
  
        
      
      <div className="breakdown-total">
        <span>Total</span>
        <span>{dayData?.total_case_count}</span>
        <span>₹{isrTargetAchieved>0 ? <> {dayData?.total_amount?.toLocaleString()}</> : 0}</span>
      </div>
    </div>
  );

export default ClientDetailModal; 